/**
 * API · core.personal_task_shares
 *
 * Sharing de tareas con scope='personal'. RLS bloquea writes que no
 * sean del owner — el cliente nunca enforce, solo el server.
 */
import { supabase } from '@/lib/supabase';
import type {
  PersonalTaskSharesInsert,
  PersonalTaskSharesRow,
  TaskSharePermission,
} from '@/types/database.types';

// personal_task_shares NO está en database.types.ts hand-curated
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export async function listSharesForTask(
  taskId: string,
): Promise<PersonalTaskSharesRow[]> {
  const { data, error } = await db
    .from('personal_task_shares')
    .select('*')
    .eq('task_id', taskId)
    .order('shared_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as PersonalTaskSharesRow[];
}

export async function addShare(input: PersonalTaskSharesInsert): Promise<void> {
  const { error } = await db.from('personal_task_shares').insert(input);
  if (error) throw error;
}

export async function removeShare(taskId: string, userId: string): Promise<void> {
  const { error } = await db
    .from('personal_task_shares')
    .delete()
    .eq('task_id', taskId)
    .eq('user_id', userId);
  if (error) throw error;
}

/**
 * Resuelve un email a su user_id via RPC core.find_user_id_by_email.
 * La RPC vive en schema core, va por SECURITY DEFINER y solo expone uuid
 * (nunca el email mismo) — el caller decide qué hacer con el resultado.
 * Devuelve null si el email no existe en auth.users.
 */
export async function resolveUserIdByEmail(email: string): Promise<string | null> {
  const { data, error } = await (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    supabase.schema('core' as any) as any
  ).rpc('find_user_id_by_email', { p_email: email.trim().toLowerCase() });
  if (error) throw error;
  return (data as string | null) ?? null;
}

export interface ShareTaskByEmailParams {
  taskId: string;
  email: string;
  sharedBy: string;
  permission?: TaskSharePermission;
}

/**
 * Helper de alto nivel: dado email + taskId, lo resuelve y crea el share.
 * Lanza error con mensaje user-friendly si el email no está registrado.
 */
export async function shareTaskByEmail({
  taskId,
  email,
  sharedBy,
  permission = 'view',
}: ShareTaskByEmailParams): Promise<void> {
  const userId = await resolveUserIdByEmail(email);
  if (!userId) {
    throw new Error(
      `No encontramos un usuario con ese email. Tiene que tener cuenta primero.`,
    );
  }
  if (userId === sharedBy) {
    throw new Error('Esa tarea ya es tuya · no podés compartírtela.');
  }
  await addShare({
    task_id: taskId,
    user_id: userId,
    shared_by: sharedBy,
    permission,
  });
}
